import { useState, useEffect } from 'react'
import type { SubDestMasterRequest, SubDestMasterResponse } from '../types/master'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

async function fetchSubDestinationsAPI(): Promise<SubDestMasterResponse[]> {
  const response = await fetch(`${API_BASE_URL}/api/sub-dest`, {
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }
  return response.json()
}

async function createSubDestinationAPI(data: SubDestMasterRequest): Promise<SubDestMasterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/sub-dest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function updateSubDestinationAPI(id: number, data: SubDestMasterRequest): Promise<SubDestMasterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/sub-dest/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export const useSubDestMaster = () => {
  const [subDestinations, setSubDestinations] = useState<SubDestMasterResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSubDestinations = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('Fetching sub destinations from:', `${API_BASE_URL}/api/sub-dest`)
      
      const data = await fetchSubDestinationsAPI()
      console.log('Sub destinations fetched successfully:', data.length)
      setSubDestinations(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      console.error('Error fetching sub destinations:', errorMessage)
      setError(errorMessage)
      setSubDestinations([])
    } finally {
      setLoading(false)
    }
  }

  const createSubDestination = async (data: SubDestMasterRequest): Promise<SubDestMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const newSubDestination = await createSubDestinationAPI(data)
      setSubDestinations(prev => [...prev, newSubDestination])
      return newSubDestination
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateSubDestination = async (
    id: number,
    data: SubDestMasterRequest
  ): Promise<SubDestMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const updatedSubDestination = await updateSubDestinationAPI(id, data)
      setSubDestinations(prev => prev.map(subDest => (subDest.id === id ? updatedSubDestination : subDest)))
      return updatedSubDestination
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteSubDestination = async (id: number): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/sub-dest/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to delete sub destination: ${response.statusText}`)
      }

      setSubDestinations(prev => prev.filter(subDest => subDest.id !== id))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getSubDestinationById = async (id: number): Promise<SubDestMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/sub-dest/${id}`, {
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch sub destination: ${response.statusText}`)
      }
      const data = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const loadSubDestinations = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('Loading sub destinations...')

        const response = await fetch(`${API_BASE_URL}/api/sub-dest`, {
          credentials: 'include',
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (isMounted) {
          console.log('Sub destinations loaded successfully:', data.length)
          setSubDestinations(data)
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
          console.error('Error loading sub destinations:', errorMessage)
          setError(errorMessage)
          setSubDestinations([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadSubDestinations()

    return () => {
      isMounted = false
    }
  }, [])

  return {
    subDestinations,
    loading,
    error,
    refetch: fetchSubDestinations,
    createSubDestination,
    updateSubDestination,
    deleteSubDestination,
    getSubDestinationById,
  }
}